import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { Search as SearchIcon, User as UserIcon, Building2, Briefcase, Wrench } from "lucide-react";

export default function Search() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") ?? "";
  const [query, setQuery] = useState(initial);

  const trimmed = query.trim();
  const enabled = trimmed.length >= 2;

  const { data, isLoading, isError } = trpc.admin.search.global.useQuery(
    { query: trimmed, limit: 10 },
    { enabled, staleTime: 30_000 }
  );

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (trimmed.length >= 2) {
      setParams({ q: trimmed });
    }
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <SearchIcon className="h-6 w-6" />
            Recherche globale
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Cherchez un artisan, particulier, entreprise ou projet par nom, email, téléphone, SIRET, ville…
          </p>
        </div>

        <form onSubmit={onSubmit} className="flex gap-2 max-w-3xl">
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Tapez 2 caractères ou plus…"
          />
          <Button type="submit" disabled={trimmed.length < 2}>
            Chercher
          </Button>
        </form>

        {!enabled && (
          <p className="text-sm text-muted-foreground">Saisissez au moins 2 caractères puis appuyez sur Entrée.</p>
        )}

        {enabled && isLoading && (
          <p className="text-sm text-muted-foreground">Recherche en cours…</p>
        )}

        {enabled && isError && (
          <p className="text-sm text-destructive">Erreur lors de la recherche.</p>
        )}

        {enabled && data && (
          <div className="grid gap-6 md:grid-cols-2">
            {/* Users */}
            <Card>
              <CardContent className="pt-5 space-y-3">
                <div className="flex items-center gap-2">
                  <UserIcon className="h-4 w-4 text-muted-foreground" />
                  <h2 className="font-semibold">Utilisateurs</h2>
                  <Badge variant="outline">{data.totals.users}</Badge>
                </div>
                {data.users.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucun résultat</p>
                ) : (
                  <ul className="divide-y">
                    {data.users.map((u) => (
                      <li
                        key={u.id}
                        className="py-2 flex items-center gap-3 hover:bg-muted/40 rounded px-1 cursor-pointer"
                        onClick={() => {
                          if (u.companyId) navigate(`/companies/${u.companyId}`);
                        }}
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium">
                            {u.firstName ?? ""} {u.lastName ?? ""}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {u.email}{u.phone ? ` · ${u.phone}` : ""}
                          </div>
                        </div>
                        {u.isStaff && <Badge variant="default">{u.staffRole ?? "STAFF"}</Badge>}
                        {u.isBlocked && <Badge variant="destructive">Bloqué</Badge>}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Companies */}
            <Card>
              <CardContent className="pt-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <h2 className="font-semibold">Entreprises</h2>
                  <Badge variant="outline">{data.totals.companies}</Badge>
                </div>
                {data.companies.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucun résultat</p>
                ) : (
                  <ul className="divide-y">
                    {data.companies.map((c) => (
                      <li
                        key={c.id}
                        className="py-2 flex items-center gap-3 hover:bg-muted/40 rounded px-1 cursor-pointer"
                        onClick={() => navigate(`/companies/${c.id}`)}
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium">{c.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {c.siret ? `SIRET ${c.siret}` : "Sans SIRET"}
                            {c.email ? ` · ${c.email}` : ""}
                          </div>
                        </div>
                        <Badge variant="outline">{c.status}</Badge>
                        {c.plan && <Badge variant="outline">{c.plan}</Badge>}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Artisans */}
            <Card>
              <CardContent className="pt-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-muted-foreground" />
                  <h2 className="font-semibold">Artisans</h2>
                  <Badge variant="outline">{data.totals.artisans}</Badge>
                </div>
                {data.artisans.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucun résultat</p>
                ) : (
                  <ul className="divide-y">
                    {data.artisans.map((a) => (
                      <li
                        key={a.id}
                        className="py-2 flex items-center gap-3 hover:bg-muted/40 rounded px-1 cursor-pointer"
                        onClick={() => navigate(`/travauxbtp/artisans/${a.id}`)}
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium">{a.companyName}</div>
                          <div className="text-xs text-muted-foreground">
                            {a.siret ? `SIRET ${a.siret}` : "Sans SIRET"} · {a.serviceCity}
                          </div>
                        </div>
                        <Badge variant={a.verificationStatus === "VERIFIED" ? "default" : "outline"}>
                          {a.verificationStatus}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Projects */}
            <Card>
              <CardContent className="pt-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-muted-foreground" />
                  <h2 className="font-semibold">Projets particuliers</h2>
                  <Badge variant="outline">{data.totals.projects}</Badge>
                </div>
                {data.projects.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucun résultat</p>
                ) : (
                  <ul className="divide-y">
                    {data.projects.map((p) => (
                      <li
                        key={p.id}
                        className="py-2 flex items-center gap-3 hover:bg-muted/40 rounded px-1 cursor-pointer"
                        onClick={() => navigate(`/projets-particuliers?id=${p.id}`)}
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium truncate" title={p.title}>{p.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {p.category} · {p.city ?? "—"} · {p.CreatedBy?.email ?? "—"}
                          </div>
                        </div>
                        {p.isUrgent && <Badge variant="destructive">URGENT</Badge>}
                        <Badge variant="outline">{p.status}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
